<template>
  <SparkModal is-fullscreen dismissable :model-value="show" @update:model-value="closeModal">
    <template #header>
      <div class="flex flex-col p-20">
        <div class="text-18 font-bold border-gray-100">Add Parts</div>
        <div class="text-13 text-gray-500">Select parts to add to calibration</div>
      </div>
    </template>

    <div class="flex">
      <div class="sidebar-title" :style="{ width: filterSideBarWidth }">
        <i
          class="chevron-icon"
          :class="[filterSideBarExpanded ? 'fas fa-circle-chevron-left fa-xl' : 'fas fa-circle-chevron-right fa-xl']"
          @click="setFilterSideBarExpanded"
        />
        <transition name="accordion" @enter="start" @after-enter="end" @before-leave="start" @after-leave="end">
          <div v-show="filterSideBarExpanded">
            <div class="library-header">
              <div class="part-library__action-buttons">
                <div class="filter-header">
                  <div class="flex justify-between items-center w-full">
                    <span class="filter icon-button" @click="filtering"><i class="fas fa-filter" />Apply</span>
                    <Reset @resetting="resetLibrary" />
                  </div>
                </div>
              </div>
              <PartLibraryFilter :reset="reset" />
            </div>
          </div>
        </transition>
      </div>

      <div v-if="show" class="library-table">
        <PartTableLight 
          :filter-key="filterKey" 
          :initial-selected-parts="existingSelectedParts"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-8">
        <SparkButton variant="secondary" @click="closeModal">Cancel</SparkButton>
        <SparkButton variant="primary" @click="addSelectedParts">Add Selected Parts</SparkButton>
      </div>
    </template>
  </SparkModal>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';

import PartLibraryFilter from '../../../views/Library/part/components/PartLibraryFilter.vue';
import PartTableLight from '../../../components/PartLibrary/PartTableLight.vue';
import Reset from '../../../components/Reusable/Filter/Reset.vue';

import SparkButton from '@/components/SparkComponents/SparkButton.vue';
import SparkModal from '@/components/SparkComponents/SparkModal.vue';

export default {
  name: 'AddPartsModal',

  components: {
    PartLibraryFilter,
    Reset,
    SparkModal,
    SparkButton,
    PartTableLight,
  },

  props: {
    show: {
      type: Boolean,
      required: true,
    },
    existingSelectedParts: { type: Array, default: () => [] },
  },

  emits: ['close', 'add-parts'],

  data() {
    return {
      filterKey: 0,
      reset: 0,
      filterSideBarExpanded: false,
      filterSideBarWidth: '2%',
      chevronColor: 'var(--accent-color)',
    };
  },

  computed: {
    ...mapState('calibration', ['selectedParts', 'selectedMode']),
    ...mapState(['partLibraryFilters']),
  },

  watch: {
    show(newValue) {
      if (newValue) {
        // Reset sidebar state when opening the modal
        this.filterSideBarExpanded = false;
        this.filterSideBarWidth = '2%';

        // Reset filters and force refresh of part library

        setTimeout(() => {
          this.filtering();
        }, 100);
      }
    },
  },

  methods: {
    ...mapMutations('calibration', ['setTransformedPartsData']),
    ...mapActions(['fetchPartLibraryData']),

    closeModal() {
      this.filterSideBarExpanded = false;
      this.filterSideBarWidth = '2%';
      this.$emit('close');
    },

    filtering() {
      this.filterKey += 1;

      // Explicitly fetch parts data
      const formData = { ...this.partLibraryFilters, page: 1 };
      this.fetchPartLibraryData(formData).catch(error => {
        console.error('Error fetching part library data:', error);
      });
    },

    resetLibrary() {
      this.reset += 1;
      setTimeout(() => {
        this.filtering();
      }, 100);
    },

    start(el) {
      el.style.width = el.scrollWidth + 'px';
    },

    end(el) {
      el.style.width = '';
    },

    setFilterSideBarExpanded() {
      console.log('setFilterSideBarExpanded');
      this.filterSideBarExpanded = !this.filterSideBarExpanded;
      if (this.filterSideBarExpanded) {
        this.filterSideBarWidth = '25%';
      } else {
        this.filterSideBarWidth = '2%';
      }
    },

    addSelectedParts() {
      if (this.selectedParts && this.selectedParts.length > 0) {
        const newParts = this.selectedParts.map(part => {
          return {
            part_id: part.part_id,
            part_name: part.name,
            lot_size: 1,
            value: this.selectedMode === 'price' ? 0 : '00:00:00', // Default value based on mode
            created_date: this.$dayjs().format('DD.MM.YYYY'),
            uid: null, // Will be set by backend after creation
          };
        });

        this.$emit('add-parts', newParts);
        this.closeModal();
      } else {
        this.$root.notify('warning', 'Please select at least one part', 3000);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.chevron-icon {
  position: absolute;
  right: -10px;
  top: 30px;
  color: v-bind(chevronColor);
  cursor: pointer;
}

.library-header {
  display: flex;
  flex-direction: column;
  margin: var(--grid-outer-whitespace);
  background-color: white;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  width: 300px;
}

.sidebar-title {
  position: relative;
  border-right: 1px solid var(--light-color);
  transition: width 0.3s ease;
}

.accordion-enter-active,
.accordion-leave-active {
  transition:
    width 0.3s ease-in,
    opacity 0.3s ease;
}

.accordion-enter,
.accordion-leave-to {
  width: 0 !important;
  opacity: 0;
}

.library-table {
  padding: var(--grid-outer-whitespace);
  margin-top: var(--grid-outer-whitespace);
  padding-top: 0px;
  background-color: white;
  padding-bottom: 30px;
  width: calc(100% - v-bind(filterSideBarWidth)) !important;
  overflow: hidden;
  transition: width 0.3s ease;
}

.part-library__action-buttons {
  display: flex;
  align-items: center;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 32px;
}

.filter {
  cursor: pointer;
  height: fit-content;
  display: flex;
  align-items: center;
}
</style>
